import dayjs from "dayjs";
import React, { useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDeleteEvent, useGetEvent, useUpdateEvent } from "../api/events-api";
import { queryClient } from "../api/queryClient";
import EventForm, { EventFormValues } from "../components/events/EventForm";
import { AdminHeader } from "../components/dss/Primitives";
import { Icon } from "../components/dss/Icon";
import { FadeIn, useEnterAnimation } from "../components/dss/Motion";
import { Skeleton } from "../components/dss/Skeleton";
import { assertIsDefined } from "../utils/assertion";

const EditEvent: React.FC = () => {
  const { eventId } = useParams();
  const { data: event, isLoading } = useGetEvent(eventId);
  const { mutateAsync: updateEvent } = useUpdateEvent();
  const { mutateAsync: deleteEvent } = useDeleteEvent();
  const navigate = useNavigate();
  const [showDeleteConfirmation, setShowDelete] = useState(false);
  const animateIn = useEnterAnimation(isLoading || !event);

  const onSubmit = useCallback(
    async (formValues: EventFormValues) => {
      assertIsDefined(eventId);
      await updateEvent({ eventId: Number(eventId), formValues });
      queryClient.invalidateQueries();
      navigate("/edit");
    },
    [eventId, navigate, updateEvent],
  );

  const onDeleteEvent = useCallback(async () => {
    if (!eventId) return;
    await deleteEvent(Number(eventId));
    queryClient.invalidateQueries(["events"]);
    navigate("/edit");
  }, [deleteEvent, eventId, navigate]);

  return (
    <div className="mx-auto max-w-md bg-paper pt-safe min-h-app">
      <AdminHeader
        back="Admin"
        onBack={() => navigate("/edit")}
        title={<>Redigér<br />samling</>}
        sub={event ? `${event.title.toUpperCase()} · N° ${eventId}` : undefined}
        action={
          <button
            onClick={() => navigate(`/vote/${eventId}/edit`)}
            className="font-mono text-[10.5px] tracking-[1.6px] uppercase text-bilberry font-semibold flex items-center gap-1.5"
          >
            <span>Afstemning</span>
            <Icon name="chevron-right" size={10} />
          </button>
        }
      />
      <div className="px-7 pt-5 pb-8">
        {isLoading || !event ? (
          <div className="flex flex-col gap-5">
            <Skeleton className="w-full h-9" />
            <div className="grid grid-cols-2 gap-4">
              <Skeleton className="h-[46px] rounded-lg" />
              <Skeleton className="h-[46px] rounded-lg" />
            </div>
            <div className="flex gap-2">
              <Skeleton className="flex-1 h-[58px] rounded-lg" />
              <Skeleton className="flex-1 h-[58px] rounded-lg" />
              <Skeleton className="flex-1 h-[58px] rounded-lg" />
            </div>
            <Skeleton className="w-20 h-11 rounded-lg" />
            <Skeleton className="w-full h-[46px] rounded-lg" />
            <div className="flex gap-1.5">
              <Skeleton className="flex-1 h-10 rounded-lg" />
              <Skeleton className="flex-1 h-10 rounded-lg" />
              <Skeleton className="flex-1 h-10 rounded-lg" />
            </div>
            <Skeleton className="w-full h-14 rounded-lg" />
          </div>
        ) : (
          <FadeIn enabled={animateIn}>
            <EventForm
              initialValues={{
                title: event.title,
                date: dayjs(event.date).format("YYYY-MM-DD"),
                time: dayjs(event.date).format("HH:mm"),
                emoji: event.emoji,
                description: event.description ?? "",
                host: event.host,
                status: event.status,
                showVoteLink: event.show_vote_link,
              }}
              onSubmit={onSubmit}
            >
              {showDeleteConfirmation ? (
                <button
                  type="button"
                  onClick={onDeleteEvent}
                  className="w-full py-3.5 rounded-lg bg-clay text-paper font-display text-[13px] font-bold uppercase tracking-wide"
                >
                  Bekræft sletning
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowDelete(true)}
                  className="w-full py-3.5 rounded-lg border border-clay text-clay font-display text-[13px] font-bold uppercase tracking-wide flex items-center justify-center gap-2"
                >
                  <Icon name="trash-can" variant="regular" size={13} />
                  Slet samling
                </button>
              )}
            </EventForm>
          </FadeIn>
        )}
      </div>
    </div>
  );
};

export default EditEvent;
